import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { styled } from '@mui/system';
import MuiSnackbar from '@mui/material/Snackbar';
import Slide from '@mui/material/Slide';
import { isMobileMedia } from 'bento-ordering/utils/media';
import { selectImageUrlForAddedCartItem } from 'bento-ordering/menus/selectors';
import { OPACITY_13 } from 'constants/opacity';
import { calculateRgbaWithAlpha } from 'helpers/colorFormatter';
import SuccessfulAddSnackbarMessage from 'bento-ordering/base/components/Snackbar/helpers/SuccessfulAddSnackbarMessage';
import { useLayout } from 'bento-ordering/context/LayoutContext';
import muiPropForwarding from 'styles/helpers/muiPropForwarding';

const UpsellDesktopSnackbar = styled(MuiSnackbar, {
  shouldForwardProp: muiPropForwarding('bottomNavHeight')
})(({ theme, bottomNavHeight }) => ({
  '&.MuiSnackbar-root': {
    maxWidth: '100%',
    position: 'fixed',
    zIndex: 1,
    '& .MuiPaper-elevation6': {
      boxShadow: `${`inset 0px 1px 0px ${calculateRgbaWithAlpha(
        theme.palette.sectionNav.link.main,
        OPACITY_13
      )}`}`
    },
    bottom: bottomNavHeight,
    right: '0'
  },
  '& .MuiSnackbarContent-root': {
    padding: '8px 0',
    width: '25rem',
    backgroundColor: theme.palette.background.default,
    color: 'red',
    flexGrow: 'initial',
    borderRadius: '0rem',
    boxShadow: 'none',
    alignContent: 'flex-start',
    height: 'auto'
  },
  '& .MuiSnackbarContent-message': {
    padding: '8px 20px',
    width: '100%'
  }
}));

const UpsellMobileSnackbar = styled(MuiSnackbar, {
  shouldForwardProp: muiPropForwarding('bottomNavHeight')
})(({ theme, bottomNavHeight }) => ({
  '&.MuiSnackbar-root': {
    maxWidth: '100%',
    position: 'fixed',
    bottom: bottomNavHeight,
    zIndex: 1,
    left: '0',
    right: '0',
    '& .MuiPaper-elevation6': {
      boxShadow: `${`inset 0px 1px 0px ${calculateRgbaWithAlpha(
        theme.palette.sectionNav.link.main,
        OPACITY_13
      )}`}`
    },
    width: '100%'
  },
  '& .MuiSnackbarContent-root': {
    position: 'fixed',
    bottom: bottomNavHeight,
    padding: '8px 20px',
    width: '100%',
    backgroundColor: theme.palette.background.default,
    color: 'red',
    flexGrow: 'initial',
    borderRadius: '0rem',
    boxShadow: 'none'
  },
  '& .MuiSnackbarContent-message': {
    padding: '8px 0',
    width: '100%'
  }
}));

const SuccessfulUpsellAddSnackbar = ({
  item,
  cartItems,
  addedItemImage,
  handleSnackbarClose
}) => {
  // using local state here for MUI transition upon entering AND exiting screen
  // - did not seem to work properly using lastItem redux store.
  const [open, setOpen] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);

  /**
   * Used to delay rendering of snackbar animation so that transition is visible.
   * Need to do this since we are rendering this inside the cart drawer.
   */
  useEffect(() => {
    const timeout = setTimeout(() => {
      setOpen(true);
      setHasMounted(true);
    }, 700);

    return () => clearTimeout(timeout);
  }, []);

  /**
   * We delay calling handleSnackbarClose until after the snackbar exit animation
   * has had time to complete. This is because handleSnackbarClose dispatches
   * a redux action to reset the lastItem store, which causes a re-render and
   * prevents the exit animation from animating.
   */
  useEffect(() => {
    if (!open && hasMounted) {
      const timeout = setTimeout(() => {
        handleSnackbarClose();
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [open, hasMounted, handleSnackbarClose]);

  const handleClose = () => {
    setOpen(false);
  };

  const transitionDelay = 350;
  const image = addedItemImage ? addedItemImage.url : null;
  const { bottomNavHeight } = useLayout();

  const commonProps = {
    autoHideDuration: 3000,
    transitionDuration: {
      enter: transitionDelay,
      exit: transitionDelay
    },
    open: open,
    onClose: handleClose,
    TransitionComponent: Slide,
    bottomNavHeight: bottomNavHeight
  };

  const mobileMedia = isMobileMedia();

  const successfulUpsellAddSnackbar = mobileMedia ? (
    <UpsellMobileSnackbar
      data-testid="mobile-successful-add-upsell-item-snackbar"
      {...commonProps}
      message={
        <SuccessfulAddSnackbarMessage
          item={item}
          image={image}
          cartItems={cartItems}
        />
      }
    />
  ) : (
    <UpsellDesktopSnackbar
      data-testid="desktop-successful-add-upsell-item-snackbar"
      {...commonProps}
      message={
        <SuccessfulAddSnackbarMessage
          item={item}
          image={image}
          cartItems={cartItems}
        />
      }
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
    />
  );

  return successfulUpsellAddSnackbar;
};

const mapStateToProps = state => {
  return {
    cartItems: state.cart.items,
    addedItemImage: selectImageUrlForAddedCartItem(state)
  };
};

export default connect(mapStateToProps)(SuccessfulUpsellAddSnackbar);
